import { GetStaticProps } from "next";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";

import { useSessionProfile } from "@saas/account/feature/@hook/session-profile/use-session-profile";
import { AccountActions } from "@saas/account/feature/account-actions/account-actions";
import { AccountMenu } from "@saas/account/feature/account-menu/account-menu";
import { LoginForm } from "@saas/account/feature/login-form/login-form";
import { accessRouteMapping } from "@saas/account/utils/access-route-mapping/access-route-mapping";
import { SessionMetadata } from "@saas/account/utils/get-profile-from-session/get-profile-from-session";
import { sortRoute } from "@saas/account/utils/sort-route/sort-route";
import { AppHead } from "@saas/layout/ui/app-head/app-head";
import { BaseLayout } from "@saas/layout/ui/base-layout/base-layout";
import { Header } from "@saas/layout/ui/header/header";
import { NotificationBar } from "@saas/shared/ui/notification-bar/notification-bar";
import { useSafePush } from "@saas/shared/utils/@hook/safe-push/use-safe-push";

import { NextPageWithLayout } from "../_app";

import { Session } from "@ory/client";

export const LoginPage: NextPageWithLayout = () => {
  const router = useRouter();
  const { profile } = useSessionProfile();
  const { safePush } = useSafePush();

  const isMarket = !!router.query.login_challenge;

  const redirectLoggedIn = useCallback(
    (data?: { session: Session }) => {
      if (router.query.redirect) {
        return safePush(router.query.redirect as string);
      }

      if (
        data?.session?.identity?.metadata_public &&
        data.session.identity.metadata_public["accesses"]
      ) {
        const metadata = data.session.identity
          .metadata_public as SessionMetadata;
        const subaccountAccesses = sortRoute(metadata.accesses);

        const parentUserId = metadata.parent_user_id;
        const firstRoute = accessRouteMapping[subaccountAccesses[0]];

        if (parentUserId !== null && firstRoute) {
          return safePush(firstRoute);
        }
      }

      return safePush("/order");
    },
    [router, safePush]
  );

  const loginMessage = (
    <span className={"flex flex-col pr-10"}>
      <span>Jika kamu sudah memiliki akun Melaka,</span>
      <span>silakan lakukan login terlebih dahulu.</span>
    </span>
  );

  const loginSubtitle = isMarket ? (
    <NotificationBar message={loginMessage} variant={"warning"} />
  ) : (
    <p className={"body-b1 text-he"}>
      Selamat datang kembali! Masukkan email dan kata sandi terdaftarmu.
    </p>
  );

  useEffect(() => {
    if (profile && !isMarket) {
      redirectLoggedIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  return (
    <>
      <AppHead>Login</AppHead>

      <div className={"flex h-full justify-center md:items-center"}>
        <div
          className={"bg-neutral-N0 mx-6 mt-5 h-max rounded-2xl p-6 md:p-24"}
        >
          <div className={"flex flex-col gap-4 md:max-w-[22.5rem] md:gap-6"}>
            <div className={"flex flex-col gap-4"}>
              <div data-testid={"login_text__title"}>
                <h1 className={"title-large"}>Login</h1>
              </div>

              <div data-testid={"login_text__subtitle"}>{loginSubtitle}</div>
            </div>

            <LoginForm testid={"login"} onSuccess={redirectLoggedIn} />
          </div>
        </div>
      </div>
    </>
  );
};

LoginPage.getLayout = (page, _, flags) => {
  return (
    <BaseLayout
      className={"bg-blue-B500"}
      header={
        <Header
          rightNavBar={
            <AccountActions
              accountMenu={AccountMenu}
              showActionButtons={false}
            ></AccountActions>
          }
          sticky={true}
          transparent={true}
          whiteMenu={true}
        />
      }
    >
      {page}
    </BaseLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};

export default LoginPage;
